/* import __COLOCATED_TEMPLATE__ from './graph-editor.hbs'; */
/* RESPONSIBLE TEAM: team-proactive-support */
import { isEmpty } from '@ember/utils';
import { throttle, debounce } from '@ember/runloop';
import { action } from '@ember/object';
import { A } from '@ember/array';
import Component from '@glimmer/component';
import Coordinates from '../models/graph-editor/coordinates';
import Offset, { type SimpleOffset } from '../models/graph-editor/offset';
import type Graph from '../models/graph-editor/graph';
import type Node from '../models/graph-editor/node';
import type Bounds from '../models/graph-editor/bounds';
import WheelEventDecoder, {
  type FullWheelEvent,
  GridWheelEvent,
} from '../lib/graph-editor/wheel-event-decoder';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import { MoveNode } from '../models/graph-editor/action';
//@ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';

const SPACEBAR = ' ';
const EDITABLE_NODE_TYPES = ['INPUT', 'TEXTAREA'];
const MIDDLE_MOUSE_BUTTON = 1;
/*
 * Any – All mouse gestures, including trackpad panning and mouse wheel zooming are allowed
 * Direct Only – Only direct mouse interactions are allowed, e.g. click and drag to pan. Zooming must use the UI controls.
 *               This is useful in circumstances where the graph is rendered in a scrollable container and we do not want
 *               to intercept scroll events.
 */
enum InteractionMode {
  Any,
  DirectOnly,
}

interface Args {
  graph: Graph;
  interactionMode: 'any' | 'direct-only';
}

// Safari's non-standard touch pad event API
interface GestureEvent extends MouseEvent {
  scale: number;
}

export default class GraphEditorComponent extends Component<Args> {
  wheelEventDecoder: WheelEventDecoder = new WheelEventDecoder();
  initialGestureScale!: number;
  deltaBuffer: Array<SimpleOffset> = [];

  get gridStyle() {
    let state = this.state;
    return sanitizeHtml(`
      --offsetX: ${Number(state.scaleTranslate.x)}px;
      --offsetY: ${Number(state.scaleTranslate.y)}px;
      --scale: ${Number(state.scale)};
      --originX: ${Number(state.transformOrigin.x)}px;
      --originY: ${Number(state.transformOrigin.y)}px;
      --grid-spacing: ${Number(this.args.graph.settings.grid.spacing)}px;
      --grid-height: ${Number(this.args.graph.settings.grid.height)}px;
      --grid-width: ${Number(this.args.graph.settings.grid.width)}px;
    `);
  }

  @action setInitialScrollOffset(): void {
    this.args.graph.setScrollOffset(
      new Offset(this.args.graph.settings.grid.width, this.args.graph.settings.grid.height),
    );
  }

  get state() {
    return this.args.graph.state;
  }

  @computed('args.interactionMode')
  get interactionMode(): InteractionMode {
    if (this.args.interactionMode === 'direct-only') {
      return InteractionMode.DirectOnly;
    }
    return InteractionMode.Any;
  }

  @action onDocumentKeyDown(event: KeyboardEvent): void {
    if (
      event.key === SPACEBAR &&
      !this.state.inPanningMode &&
      !this.isTargetEditableElement(event.target)
    ) {
      this.state.inPanningMode = true;
    }

    if (event.target === document.body && event.key === SPACEBAR) {
      event.preventDefault();
    }
  }

  @action onDocumentKeyUp(event: KeyboardEvent): void {
    if (event.key === SPACEBAR && this.state.inPanningMode) {
      this.state.inPanningMode = false;
    }
  }

  @action onDocumentMouseUp(_: MouseEvent): void {
    this.state.lastPanningCoordinates = undefined;
    this.state.selectionStartCoordinates = undefined;
  }

  @action onDragOver(event: DragEvent): void {
    event.preventDefault();
    throttle(
      this,
      this.handleDragOver,
      event,
      false,
      this.args.graph.settings.performance.throttle,
    );
  }

  private isTargetEditableElement(target: EventTarget | null): boolean {
    if (target instanceof HTMLElement) {
      return EDITABLE_NODE_TYPES.includes(target.nodeName) || target.isContentEditable;
    }
    return false;
  }

  private handleDragOver(event: DragEvent): void {
    this.updateMouseCoordinates(event);

    if (this.state.selectedNodes) {
      let currentCoordinates: Coordinates = Coordinates.scaledAndRounded(
        event.clientX,
        event.clientY,
        this.args.graph,
      );

      let coordinateDiff: Offset = new Offset(
        currentCoordinates.x - this.state.positioningCoordinates.x,
        currentCoordinates.y - this.state.positioningCoordinates.y,
      );

      // If we there's no difference between the current mouse coordinates
      // and the last positioning coordinates do not bother updating.
      if (!coordinateDiff.isZero) {
        this.state.selectedNodes.forEach((node: Node) => {
          if (this.args.graph.hooks.canUpdateNode(node, coordinateDiff)) {
            node.position.x += coordinateDiff.x;
            node.position.y += coordinateDiff.y;
            node.calculateBounds();
            this.args.graph.recordAction(new MoveNode(node, coordinateDiff.x, coordinateDiff.y));
            this.args.graph.hooks.didUpdateNode(node);
          }
        });
      }

      // Update the reference to the current coordinates
      this.state.positioningCoordinates = currentCoordinates;
    }
  }

  @action onMouseMove(event: MouseEvent): void {
    let state = this.state;

    if (state.lastPanningCoordinates) {
      let currentCoordinates = new Coordinates(event.clientX, event.clientY);

      let coordinateDiff: SimpleOffset = {
        x: currentCoordinates.x - state.lastPanningCoordinates.x,
        y: currentCoordinates.y - state.lastPanningCoordinates.y,
      };

      let offset: SimpleOffset = {
        x: this.args.graph.wrapper!.scrollLeft - coordinateDiff.x,
        y: this.args.graph.wrapper!.scrollTop - coordinateDiff.y,
      };

      this.args.graph.setScrollOffset(offset);

      state.lastPanningCoordinates = currentCoordinates;
    } else {
      throttle(
        this,
        this.handleMouseMove,
        event,
        false,
        this.args.graph.settings.performance.throttle,
      );
    }
  }

  @action onMouseDown(event: MouseEvent): void {
    if (event.button === MIDDLE_MOUSE_BUTTON) {
      this.state.inPanningMode = true;
    }

    if (this.state.inPanningMode) {
      this.state.lastPanningCoordinates = new Coordinates(event.clientX, event.clientY);
    } else {
      let coordinates = Coordinates.scaled(event.clientX, event.clientY, this.args.graph);
      let nodesAtCoordinates = this.args.graph.nodes.filter((node) =>
        node.bounds.coversCoordinates(coordinates),
      );
      // If we have no selected nodes at the current coordinates (i.e. we're not attempting to drag a block of nodes)
      // and we cannot add nodes to a previous selection then deselect all the selected nodes.
      if (
        !nodesAtCoordinates.some((node) => node.isSelected) &&
        !this.args.graph.hooks.canAddNodesToSelection(event)
      ) {
        this.state.selectedNodes.clear();
      }

      this.args.graph.state.selectedEdge = undefined;

      // If there are no nodes at the current coordinates then we can begin selection from this point
      if (isEmpty(nodesAtCoordinates)) {
        this.state.selectionStartCoordinates = coordinates;
      }
    }
  }

  @action onMouseUp(event: MouseEvent): void {
    this.state.lastPanningCoordinates = undefined;
    this.state.selectionStartCoordinates = undefined;

    if (event.button === MIDDLE_MOUSE_BUTTON) {
      this.state.inPanningMode = false;
    }
  }

  private handleMouseMove(event: MouseEvent): void {
    this.updateMouseCoordinates(event);
    this.updateSelectedNodes(event);
  }

  private updateMouseCoordinates(event: MouseEvent): void {
    this.state.screenCoordinates = new Coordinates(event.clientX, event.clientY);

    // On movement of the mouse over the grid we want to calculate the coordinates
    // for use in calculating things like which nodes are hovered.
    this.state.mouseCoordinates = Coordinates.scaled(event.clientX, event.clientY, this.args.graph);
  }

  private updateSelectedNodes(event?: Event): void {
    if (this.state.selectionBounds) {
      let selectedNodes = A(
        this.args.graph.nodes.filter((node) =>
          node.bounds.overlaps(this.state.selectionBounds as Bounds),
        ),
      );
      if (!this.args.graph.hooks.canAddNodesToSelection(event)) {
        this.state.selectedNodes.clear();
      }
      selectedNodes.map((node) => this.state.addSelectedNode(node));
    }
  }

  /*
   * Safari fires non-standard GestureEvents rather than "wheel" like other browsers.
   * To prevent the entire page from scaling on a pinch zoom we must preventDefault here.
   *
   * Safari's GestureEvent has a scale value relative to the start of the gesture event.
   * i.e. it always starts at 1.0 and increases or descreases in the 'gesturechange' event.
   * This means we must capture the scale of the grid at the point at which the gesture event starts
   * so that we can correctly calculate the new scale in onGestureChange.
   */

  @action onGestureStart(event: GestureEvent): void {
    this.initialGestureScale = this.state.scale;
    this.updateTransformOrigin(event);
    event.preventDefault();
  }

  @action onGestureChange(event: GestureEvent): void {
    throttle(
      this,
      this.handleGestureChange,
      event,
      false,
      this.args.graph.settings.performance.throttle,
    );
    event.preventDefault();
  }

  private handleGestureChange(event: GestureEvent): void {
    window.requestAnimationFrame(() => {
      let state = this.state;
      state.setScale(this.initialGestureScale * event.scale);
    });
  }

  @action onGestureEnd(event: GestureEvent): void {
    event.preventDefault();
  }
  // End Safari specific handlers

  /*
   * When using the scroll bar to move the graph we must ensure that we synchronize the interal
   * gridOffset representation to match the new scrollOffset.
   */
  @action onScroll(_: MouseEvent): void {
    debounce(this, this.synchronizeOffset, 50, false);
  }

  @action onWheel(event: FullWheelEvent): void {
    if (this.interactionMode === InteractionMode.DirectOnly) {
      if (this.args.graph.state.disableScroll) {
        return undefined;
      } else {
        return event.preventDefault();
      }
    }
    let state = this.state;

    let eventType = this.wheelEventDecoder.decode(
      event,
      this.args.graph.settings.scroll.inScrollWheelZoomMode,
    );

    switch (eventType) {
      case GridWheelEvent.TrackpadPan: {
        let deltaOffset: SimpleOffset = {
          x: event.deltaX,
          y: event.deltaY,
        };
        this.deltaBuffer.push(deltaOffset);
        throttle(
          this,
          this.handlePanning,
          undefined,
          false,
          this.args.graph.settings.performance.throttle,
        );
        break;
      }
      case GridWheelEvent.TrackpadZoom: {
        this.updateTransformOrigin(event);
        state.setScale(state.scale + event.deltaY * -0.01);
        break;
      }
      case GridWheelEvent.MouseWheelXPan:
      case GridWheelEvent.MouseWheelYPan: {
        let mouseWheelFactor = this.args.graph.settings.scroll.mouseWheelFactor;
        let x =
          eventType === GridWheelEvent.MouseWheelXPan ? event.wheelDelta * mouseWheelFactor : 0;
        let y =
          eventType === GridWheelEvent.MouseWheelYPan ? event.wheelDelta * mouseWheelFactor : 0;
        let deltaOffset: SimpleOffset = { x, y };
        this.deltaBuffer.push(deltaOffset);
        throttle(
          this,
          this.handlePanning,
          undefined,
          false,
          this.args.graph.settings.performance.throttle,
        );
        break;
      }
      case GridWheelEvent.MouseWheelZoom: {
        this.updateTransformOrigin(event);
        let direction = event.deltaY > 0 ? -1 : 1;
        state.setScale(state.scale + 0.1 * direction);
        break;
      }
    }
    event.preventDefault();
  }

  /*
   * Panning events are buffered. They are applied every `${performance.throttle}`ms.
   * This method takes all of the events from the buffer and applies them in one go.
   */
  private handlePanning(): void {
    if (this.args.graph.wrapper) {
      let deltaX = this.deltaBuffer.reduce((totalDelta, offset) => totalDelta + offset.x, 0);
      let deltaY = this.deltaBuffer.reduce((totalDelta, offset) => totalDelta + offset.y, 0);

      let offset = new Offset(
        this.args.graph.wrapper.scrollLeft + deltaX,
        this.args.graph.wrapper.scrollTop + deltaY,
      );
      this.args.graph.setScrollOffset(offset);
      this.deltaBuffer = [];
    }
  }

  private synchronizeOffset(): void {
    let graph = this.args.graph;
    let wrapper = this.args.graph.wrapper;
    if (wrapper) {
      graph.state.gridOffset.x = wrapper.scrollLeft * graph.state.scale;
      graph.state.gridOffset.y = wrapper.scrollTop * graph.state.scale;
    }
  }

  /*
   * If a wheel event occurs within the overlaid area we want to prevent it propagating
   * to the grid below it. This prevents the grid from panning while scrolling any content
   * that might be within the overlaid area.
   */
  @action onOverlaidAreaWheel(event: WheelEvent) {
    event.stopPropagation();
  }

  /*
   * Before zooming, we update the `transform-origin` to be the current
   * cursor coordinates. This means that when the user zooms in or out, we will zoom to
   * the point their cursor is at. (Rather than, say, 0,0).
   *
   * If a zoom is applied, changing the transform-origin will cause the `translate`
   * of the grid to also move in relation to the cusor. To prevent this, we calculate what the
   * effect of changing the old transform-origin to the new transform-origin will have on the
   * translation value and negate it in the `scaleTranslate` value of the Graph State.
   */
  private updateTransformOrigin(event: MouseEvent): void {
    let currentCoordinates = Coordinates.scaled(event.clientX, event.clientY, this.args.graph);
    this.state.updateTransformOriginForCoordinates(currentCoordinates);
  }
}
